import { useFormik } from 'formik';
import isEqual from 'lodash/isEqual';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { usePrevious, useAsync } from 'react-use';

import { useTranslation } from 'common/hooks/useTranslation';
import { useNotifications, NotificationType } from 'modules/app/notifications';
import { ActiveAccountingIntegrationApi } from 'modules/bookkeep/hooks';
import { useDesignatedCustomFieldQuery } from 'modules/custom-fields/hooks/useDesignatedCustomFieldQuery';
import { companyAPI } from 'src/core/api/axios';
import { type AppState } from 'src/core/reducers';
import { getCompanyId } from 'src/core/selectors/globalSelectorsTyped';

import { TemplateEditorForm } from './TemplateEditorForm';
import { type FormValues, getFormInitialValues, validate } from './formValues';
import { useIsAnalyticalSplitActivated } from '../../../../apis/analytical-split/useAnalyticalSplitStatus';
import { type CustomField } from '../../customField';
import * as actions from '../../redux/actions';
import { toApiTemplate } from '../../redux/reshapers';
import * as selectors from '../../redux/selectors';
import * as thunks from '../../redux/thunks';
import {
  type Template,
  type Column,
  type AvailableColumn,
  getCompositePatternPartOptions,
  searchAvailableColumns,
  type PreviewRow,
} from '../../template';

type Props = {
  companyId: string;
  templateId?: string;
  template?: Template;
  customFields: CustomField[];
  availableTemplateColumns: AvailableColumn[];
  hasLoadedTemplate: boolean;
  onCancel: () => void;
  onSave: () => void;
  fetchTemplate: (
    id: string | 'default',
    isTranslated: boolean,
  ) => Promise<void>;
  saveTemplate: (values: FormValues, templateId?: string) => Promise<void>;
  resetOpenedTemplate: () => void;
};

const TemplateEditorFormManager = ({
  companyId,
  templateId,
  template,
  customFields,
  availableTemplateColumns,
  hasLoadedTemplate,
  onCancel,
  onSave,
  fetchTemplate,
  saveTemplate,
  resetOpenedTemplate,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}: Props) => {
  const { pushNotif } = useNotifications();
  const { t } = useTranslation('global');
  const [rows, setRows] = useState<PreviewRow[]>([]);
  const [isComputingRows, setIsComputingRows] = useState<boolean>(false);
  const isTemplateEditionMode = templateId !== undefined;
  const isAnalyticalSplitActivated = useIsAnalyticalSplitActivated();
  const designatedCustomFieldQueryState = useDesignatedCustomFieldQuery();
  const othersLabel = t('exports.accountingsSoftwareOther');

  const activeAccountingIntegration =
    ActiveAccountingIntegrationApi.useActiveAccountingIntegration();

  const isDoubleOrSingleEntry =
    activeAccountingIntegration.status === 'success' &&
    (activeAccountingIntegration.data.activeAccountingIntegration ===
      'SpendeskAccounting' ||
      activeAccountingIntegration.data.activeAccountingIntegration ===
        'SpendeskAccountingSingleEntry');

  const expenseCategoryCustomFieldId =
    designatedCustomFieldQueryState.status === 'success'
      ? designatedCustomFieldQueryState.data.customFieldId
      : undefined;

  const formikProps = useFormik<FormValues>({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: getFormInitialValues({
      template,
      isDoubleOrSingleEntry,
      customFields,
      isAnalyticalSplitActivated,
      othersLabel,
    }),
    validate: (values) => {
      return validate(values, t);
    },
    onSubmit: async (values) => {
      // FIXME: we don't send tracking information for template edition as we
      // used to in CreateExport.
      try {
        await saveTemplate(values, templateId);
      } catch (error) {
        pushNotif({
          type: NotificationType.Danger,
          message: isTemplateEditionMode
            ? t('exports.messages.errorEditExport')
            : t('exports.messages.errorCreateExport'),
        });
        throw error;
      }

      pushNotif({
        type: NotificationType.Success,
        message: isTemplateEditionMode
          ? t('exports.messages.successEditExport')
          : t('exports.messages.successCreateExport'),
      });
      onSave();
    },
  });
  const previousValues = usePrevious(formikProps.values);

  useEffect(() => {
    fetchTemplate(isTemplateEditionMode ? templateId : 'default', true);

    return () => {
      resetOpenedTemplate();
    };
  }, [fetchTemplate, resetOpenedTemplate, isTemplateEditionMode, templateId]);

  // Recompute sample rows on change
  useAsync(async () => {
    if (
      !hasLoadedTemplate ||
      // Only recompute if the changes actually affect the output
      !(
        formikProps.values.dateDelimiter !== previousValues?.dateDelimiter ||
        formikProps.values.dateFormat !== previousValues?.dateFormat ||
        formikProps.values.decimalDelimiter !==
          previousValues?.decimalDelimiter ||
        formikProps.values.language !== previousValues?.language ||
        formikProps.values.rowDisplayOptions !==
          previousValues?.rowDisplayOptions ||
        !isEqual(formikProps.values.columns, previousValues?.columns)
      )
    ) {
      return;
    }

    setIsComputingRows(true);

    const { data: preview } = await companyAPI.post(
      '/accounting-export-templates/preview',
      toApiTemplate(formikProps.values, companyId),
      { companyId },
    );

    setRows(preview);
    setIsComputingRows(false);
  }, [
    formikProps.values,
    previousValues,
    companyId,
    templateId,
    hasLoadedTemplate,
  ]);

  return (
    <TemplateEditorForm
      {...formikProps}
      customFields={customFields}
      expenseCategoryCustomFieldId={expenseCategoryCustomFieldId}
      rows={rows}
      compositePatternPartOptions={getCompositePatternPartOptions({
        customFields,
        availableColumns: availableTemplateColumns,
        t,
      })}
      isTemplateEditionMode={isTemplateEditionMode}
      isLoading={!hasLoadedTemplate}
      isComputingRows={isComputingRows}
      searchAvailableColumns={(
        columnsInUse: Column[],
        query: string,
      ): Column[] =>
        searchAvailableColumns({
          columnsInUse,
          availableTemplateColumns,
          customFields,
          query,
          t,
        })
      }
      onCancel={onCancel}
    />
  );
};

const mapStateToProps = (state: AppState) => ({
  companyId: getCompanyId(state),
  template: selectors.getOpenedTemplate(state),
  availableTemplateColumns: selectors.getAvailableTemplateColumns(state),
  hasLoadedTemplate: selectors.getOpenedTemplateHasLoaded(state),
});

const mapDispatchToProps = {
  fetchTemplate: thunks.fetchTemplate,
  saveTemplate: thunks.saveTemplate,
  resetOpenedTemplate: actions.resetOpenedTemplate,
};

export const TemplateEditorFormContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(TemplateEditorFormManager);
